import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Theme, Typography } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import makeStyles from '@mui/styles/makeStyles';
import { multilineEllipsis } from '../../theme/styles/mixins';
import Link from '../Link';
import { stopEffects } from '../../lib/Event';

const useStyles = makeStyles((theme: Theme) => ({
  description: {
    color: theme.palette.text.primary,
    fontSize: theme.spacing(1.5),
    fontStyle: 'normal',
    fontWeight: 400,
    letterSpacing: '0.15px',
    lineHeight: theme.spacing(2),
    wordBreak: 'break-word',
  },
  descriptionWithCollapse: {
    ...multilineEllipsis(3),
  },
  remainingDescription: {
    display: 'inline-block',
    height: 0,
    overflow: 'hidden',
  },
}));

type Props = React.PropsWithChildren<{
  className?: string;
  text: string;
}>;

const ShowMoreLess: React.FC<Props> = (props: Props): JSX.Element => {
  const classes = useStyles();

  const { children, className, text } = props;

  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  const descriptionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = descriptionRef.current;
    if (!element) {
      return;
    }

    setIsTruncated(
      element.offsetHeight < element.scrollHeight ||
        element.offsetWidth < element.scrollWidth
    );

    return () => {
      setExpanded(false);
    };
  }, [text]);

  const toggleCollapse = () => {
    setExpanded(!expanded);
  };

  return (
    <>
      <Typography
        className={classNames(classes.description, {
          [classes.descriptionWithCollapse]: !expanded,
        })}
        component="div"
        ref={descriptionRef}
      >
        {children}
      </Typography>
      {isTruncated && (
        <Link
          className={className}
          onClick={stopEffects(toggleCollapse)}
          sx={{ pt: 2 }}
          text={`Show ${expanded ? 'less' : 'more'}`}
          variant="body2"
          afterIcon={
            expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />
          }
        />
      )}
    </>
  );
};

export default ShowMoreLess;
